import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle, Send, Plus, Search, ArrowLeft, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get WebSocket URL from API URL
const getWsUrl = () => {
  const url = new URL(API_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}`;
};

const CommunicationPage = () => {
  const { getAuthHeader, user, token } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newChatDialogOpen, setNewChatDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    fetchRooms();
    fetchContacts();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // WebSocket connection management
  const connectWebSocket = useCallback((roomId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = `${getWsUrl()}/api/chat/ws/${roomId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      setIsConnected(true);
      console.log('WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'new_message':
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
          fetchRooms(); // Update unread counts
          break;
          
        case 'typing':
          if (data.is_typing) {
            setIsTyping(data.user_name);
          } else {
            setIsTyping(null);
          }
          break;
          
        case 'user_joined':
        case 'user_left':
          setOnlineUsers(data.online_users || []);
          break;
          
        case 'messages_read':
          // Could update read receipts UI here
          break;
          
        default:
          console.log('Unknown WS message type:', data.type);
      }
    };
    
    ws.onclose = (event) => {
      setIsConnected(false);
      console.log('WebSocket closed:', event.code, event.reason);
      
      // Attempt to reconnect after 3 seconds if not intentional close
      if (event.code !== 1000 && selectedRoom) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connectWebSocket(roomId);
        }, 3000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };
    
    wsRef.current = ws;
  }, [token, selectedRoom]);

  useEffect(() => {
    if (selectedRoom && token) {
      fetchMessages(selectedRoom.id);
      connectWebSocket(selectedRoom.id);
      
      return () => {
        if (wsRef.current) {
          wsRef.current.close(1000, 'Room changed');
        }
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
  }, [selectedRoom?.id, token, connectWebSocket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/rooms`, { headers: getAuthHeader() });
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/contacts`, { headers: getAuthHeader() });
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchMessages = async (roomId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/chat/rooms/${roomId}/messages`,
        { headers: getAuthHeader() }
      );
      setMessages(response.data);
      fetchRooms(); // Update unread count
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async (contact) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/chat/rooms`,
        { participant_id: contact.id, type: 'private' },
        { headers: getAuthHeader() }
      );
      
      setSelectedRoom(response.data);
      setNewChatDialogOpen(false);
      fetchRooms();
    } catch (error) {
      toast.error('Failed to start chat');
      console.error(error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    
    // Send via WebSocket if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        message: messageText,
        message_type: 'text'
      }));
    } else {
      // Fallback to REST API
      setSendingMessage(true);
      try {
        const response = await axios.post(
          `${API_URL}/api/chat/rooms/${selectedRoom.id}/messages`,
          { message: messageText },
          { headers: getAuthHeader() }
        );
        
        setMessages(prev => [...prev, response.data]);
        fetchRooms();
      } catch (error) {
        toast.error('Failed to send message');
        setNewMessage(messageText); // Restore message
        console.error(error);
      } finally {
        setSendingMessage(false);
      }
    }
  };

  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: true
      }));
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing indicator after 2 seconds
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'typing',
            is_typing: false
          }));
        }
      }, 2000);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'teacher': return 'bg-blue-100 text-blue-800';
      case 'parent': return 'bg-green-100 text-green-800';
      case 'school_admin': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isOtherUserOnline = (room) => {
    if (!room.other_participant) return false;
    const otherId = room.participants?.find(p => p !== user?.id);
    return room.online_users?.includes(otherId) || onlineUsers.includes(otherId);
  };

  return (
    <DashboardLayout title="Communication">
      <div className="h-[calc(100vh-180px)] flex gap-4">
        {/* Sidebar - Chat List */}
        <Card className={`w-80 flex-shrink-0 flex flex-col ${selectedRoom ? 'hidden md:flex' : 'flex'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Messages
              </CardTitle>
              <Dialog open={newChatDialogOpen} onOpenChange={setNewChatDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="new-chat-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Start New Chat</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search contacts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {filteredContacts.map(contact => (
                          <button
                            key={contact.id}
                            onClick={() => handleStartChat(contact)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                            data-testid={`contact-${contact.id}`}
                          >
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-primary/10">
                                {getInitials(contact.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{contact.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${getRoleBadgeColor(contact.role)}`}>
                              {contact.role}
                            </span>
                          </button>
                        ))}
                        {filteredContacts.length === 0 && (
                          <p className="text-center text-muted-foreground py-8">No contacts found</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              <div className="px-4 pb-4 space-y-2">
                {rooms.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    No conversations yet. Start a new chat!
                  </p>
                ) : (
                  rooms.map(room => (
                    <button
                      key={room.id}
                      onClick={() => setSelectedRoom(room)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                        selectedRoom?.id === room.id ? 'bg-primary/10' : 'hover:bg-muted'
                      }`}
                      data-testid={`room-${room.id}`}
                    >
                      <div className="relative">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10">
                            {getInitials(room.other_participant?.name || room.name)}
                          </AvatarFallback>
                        </Avatar>
                        {isOtherUserOnline(room) && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate">
                            {room.other_participant?.name || room.name}
                          </p>
                          {room.unread_count > 0 && (
                            <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                              {room.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {room.last_message?.content || room.last_message?.message || 'No messages yet'}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Main Chat Area */}
        <Card className={`flex-1 flex flex-col ${!selectedRoom ? 'hidden md:flex' : 'flex'}`}>
          {selectedRoom ? (
            <>
              {/* Chat Header */}
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="md:hidden"
                    onClick={() => setSelectedRoom(null)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="relative">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10">
                        {getInitials(selectedRoom.other_participant?.name || selectedRoom.name)}
                      </AvatarFallback>
                    </Avatar>
                    {isOtherUserOnline(selectedRoom) && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      {selectedRoom.other_participant?.name || selectedRoom.name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {selectedRoom.other_participant && (
                        <p className="text-xs text-muted-foreground capitalize">
                          {selectedRoom.other_participant.role?.replace('_', ' ')}
                        </p>
                      )}
                      {isTyping && (
                        <p className="text-xs text-primary animate-pulse">
                          {isTyping} is typing...
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    {isConnected ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Wifi className="h-3 w-3" /> Live
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <WifiOff className="h-3 w-3" /> Offline
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  <AnimatePresence>
                    {messages.map((msg, index) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className={`flex mb-4 ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${msg.sender_id === user?.id ? 'order-2' : ''}`}>
                          {msg.sender_id !== user?.id && (
                            <p className="text-xs text-muted-foreground mb-1">{msg.sender?.name}</p>
                          )}
                          <div className={`rounded-2xl px-4 py-2 ${
                            msg.sender_id === user?.id 
                              ? 'bg-primary text-primary-foreground rounded-br-sm' 
                              : 'bg-muted rounded-bl-sm'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content || msg.message}</p>
                          </div>
                          <p className={`text-xs text-muted-foreground mt-1 ${
                            msg.sender_id === user?.id ? 'text-right' : ''
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start mb-4"
                    >
                      <div className="bg-muted rounded-2xl px-4 py-2 rounded-bl-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              <div className="p-4 border-t">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      handleTyping();
                    }}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                    disabled={sendingMessage}
                  />
                  <Button type="submit" disabled={!newMessage.trim() || sendingMessage} data-testid="send-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">Select a conversation</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a chat from the list or start a new one
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CommunicationPage;
